/* eslint-disable @nice-cxone/ccf/required-tsdoc */
import React, { ComponentType, useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { Route, Redirect, RouteProps } from 'react-router-dom';
import { useAdminAccess } from 'libs/react/ui-components/src/lib/ccf-app-space/ccf-copilot/useAdminAccess';
interface PrivateRouteProps extends RouteProps {
  component: ComponentType<any>;
}

/**
 * A private route component that checks if the user is authorized to access the route.
 * If the user is not authorized, it displays an unauthorized message and redirects to the homepage after a countdown.
 *
 * @param props - The props for the private route component.
 * @returns The rendered route or unauthorized message.
 * @example
 * ```tsx
 * <PrivateRoute component={AdminComponent} />
 * ```
 */

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  component: Component,
}) => {
  const [redirectCountdown, setRedirectCountdown] = useState<number>(5);
  const { isAuthorized } = useAdminAccess();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [isAuthorized]);

  useEffect(() => {
    if (!isAuthorized && !isLoading) {
      const intervalId = setInterval(() => {
        setRedirectCountdown((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(intervalId);
    }
    return;
  }, [isAuthorized, isLoading]);

  if (isLoading) {
    return null;
  }

  if (isAuthorized) {
    return <Route render={(props) => <Component {...props} />} />;
  }

  if (redirectCountdown <= 0) {
    return <Redirect to="/home" />;
  }

  return (
    <div style={{ textAlign: 'center', padding: '50px' }}>
      <h2>You are not authorized to view this page.</h2>
      <p>Redirecting to homepage in {redirectCountdown} seconds...</p>
    </div>
  );
};

export default PrivateRoute;